<template>
  <div class="chat-container">
    
  
    

    <!-- Header bleibt fixed -->
    <div class="chat-header">
      <h1>{{ translations.title }}</h1>
      <div class="header-buttons">
        <button class="icon-button icon-color"  @click="toggleLegalInfo" :title="dynamicTranslations.legal?.title || 'Rechtliche Hinweise'">
          <i class="fas fa-info-circle"></i>
        </button>
        <button class="icon-button icon-color" @click="openFeedbackModal" :title="dynamicTranslations.feedback?.title || 'Feedback geben'">
          <i class="fas fa-star-half-alt"></i>
        </button>
        <button class="icon-button icon-color" @click="clearChat" :title="translations.messages.clearConfirm || 'Chat löschen'">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>

    <!-- Legal Info Modal -->
    <div v-if="showLegalInfo" class="legal-modal-overlay" @click="toggleLegalInfo">
      <div class="legal-modal" @click.stop>
        <div class="legal-modal-header">
          <h3>{{ dynamicTranslations.legal?.title || 'Rechtliche Hinweise' }}</h3>
          <button class="close-button" @click="toggleLegalInfo">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="legal-content">

          <!-- Datenschutz -->
          <div class="legal-section">
            <h4><i class="fas fa-shield-alt"></i>
              {{ dynamicTranslations.legal?.privacy?.title || 'Datenschutz-Hinweis' }}
            </h4>
            <p>{{ dynamicTranslations.legal?.privacy?.content || 'Die Antworten werden...' }}</p>

            <h5>{{ dynamicTranslations.legal?.privacy?.feedbackTool || 'Feedback-Tool:' }}</h5>
            <p>{{ dynamicTranslations.legal?.privacy?.feedbackContent || 'Im Chatbot ist...' }}</p>

            <h5>{{ dynamicTranslations.legal?.privacy?.dataProcessing || 'Verarbeitung von Daten:' }}</h5>
            <p>{{ dynamicTranslations.legal?.privacy?.dataContent || 'Die Feedback-Daten...' }}</p>

            <h5>{{ dynamicTranslations.legal?.privacy?.gdpr || 'DSGVO-Konformität:' }}</h5>
            <p>{{ dynamicTranslations.legal?.privacy?.gdprContent || 'Da keine personenbezogenen...' }}</p>

            <h5>{{ dynamicTranslations.legal?.privacy?.security || 'Sicherheit:' }}</h5>
            <p>{{ dynamicTranslations.legal?.privacy?.securityContent || 'Wir ergreifen alle...' }}</p>
          </div>



        </div>

        <div class="legal-modal-footer">
          <button class="btn-close" @click="toggleLegalInfo">
            {{ dynamicTranslations.legal?.close || 'Schließen' }}
          </button>
        </div>

      </div>
    </div>

    <!-- Feedback Modal -->
    <div v-if="showFeedbackModal" class="feedback-modal-overlay" @click="closeFeedbackModal">
      <div class="feedback-modal" @click.stop>

        <h4>{{ dynamicTranslations.feedback?.title || 'Wie zufrieden sind Sie mit dem SCHUBERTH Chatbot?' }}</h4>

        <!-- Sterne-Bewertung -->
        <div class="rating-section">
          <p>{{ dynamicTranslations.feedback?.rating || 'Gesamtbewertung:' }}</p>
          <div class="rating-stars">
            <button
              v-for="rating in [1, 2, 3, 4, 5]"
              :key="rating"
              @click="setRating(rating)"
              class="star-btn"
              :class="{ 'active': selectedRating >= rating }"
              :title="getRatingText(rating)"
            >
              <i :class="getRatingIcon(rating)"></i>
            </button>
          </div>
          <small class="rating-text">{{ getRatingText(selectedRating) }}</small>
        </div>

        <!-- Issues Selection -->
        <div v-if="selectedRating <= 3 && selectedRating > 0" class="issues-section">
          <p>{{ dynamicTranslations.feedback?.issues || 'Was können wir verbessern?' }}</p>
          <div class="issues-grid">
            <button
              v-for="issue in issueOptions"
              :key="issue.id"
              class="issue-btn"
              :class="{ 'selected': selectedIssues.includes(issue.id) }"
              @click="toggleIssue(issue.id)"
            >
              {{ issue.label }}
            </button>
          </div>
        </div>

        <!-- Zusätzliches Feedback -->
        <div class="custom-feedback">
          <textarea
            v-model="customFeedbackText"
            :placeholder="selectedRating <= 3 ?
              (dynamicTranslations.feedback?.placeholderLow || 'Weitere Anmerkungen (optional)') :
              (dynamicTranslations.feedback?.placeholderHigh || 'Was hat Ihnen besonders gut gefallen? (optional)')"
            rows="3"
          />
        </div>

        <div class="modal-actions">
          <button @click="closeFeedbackModal" class="btn-cancel">
            {{ dynamicTranslations.feedback?.cancel || 'Abbrechen' }}
          </button>

          <button
            @click="submitFeedback"
            class="btn-submit"
            :disabled="selectedRating === 0 || (selectedRating <= 3 && selectedIssues.length === 0)"
          >
            {{ dynamicTranslations.feedback?.submit || 'Feedback senden' }}
          </button>
        </div>

      </div>
    </div>

    <!-- Chatbereich -->
    <div class="chat-main">

      <div class="chat-box" ref="chatBox" @scroll="onUserScroll">

        <!-- ── Inline Datenschutzhinweis ── -->
        <div v-if="showPrivacyBanner" class="privacy-inline-banner">
          <div class="privacy-inline-header">
            <i class="fas fa-shield-alt"></i>
            <span>{{ privacyText.title }}</span>
          </div>
          <div class="privacy-inline-body">
            <p>{{ privacyText.body }}</p>
            <p class="privacy-inline-warning">
              <i class="fas fa-exclamation-triangle"></i>
              {{ privacyText.warning }}
            </p>
            <p>
              {{ privacyText.link }}
              <a href="https://www.schuberth.com/de/datenschutz"
                 target="_blank" rel="noopener noreferrer"
                 class="privacy-inline-link">{{ privacyText.linkText }}</a>
              {{ privacyText.section }}
            </p>
          </div>
          <button class="privacy-inline-btn" @click="acceptPrivacyBanner">
            <i class="fas fa-check"></i>
            {{ privacyText.btn }}
          </button>
        </div>
        <!-- ── Ende Datenschutzhinweis ── -->
        <template v-if="!showPrivacyBanner">
        <ChatMessage
          v-for="(msg, index) in messages"
          :key="index"
          :message="msg"
          :session-id="sessionId"
          :messages="messages"
          @scroll-to-bottom="handleScrollToBottom"
        />
        <div v-if="isTyping" class="message bot-message">
          <div class="bot-avatar"><img 
              src="@/assets/schuberth-logo-small.svg" 
              alt="Weihnachtsbaum"
              class="tree-image"
           
            />
           </div>
          <div class="message-content">
            <div class="typing-indicator">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
        </template>
      </div>

      <div class="chat-input-container">

        <!-- Sprache -->
        <div class="language-dropdown">
          <button
            class="dropdown-trigger"
            @click="toggleLanguageDropdown"
            :title="`Current language: ${getSelectedLanguage().label}`"
          >
            <span class="selected-flag">{{ getSelectedLanguage().flag }}</span>
            <i class="fas fa-chevron-down dropdown-arrow" :class="{ rotated: showLanguageDropdown }"></i>
          </button>

          <div v-if="showLanguageDropdown" class="dropdown-menu">
            <div
              v-for="lang in availableLanguages"
              :key="lang.value"
              @click="selectLanguage(lang.value)"
              :class="['dropdown-item', { active: selectedLanguage === lang.value }]"
            >
              <span class="flag-icon">{{ lang.flag }}</span>
              <span class="language-name">{{ lang.label }}</span>
            </div>
          </div>
        </div>

        <!-- Eingabefeld -->
        <div class="chat-input">
          <input
            type="text"
            v-model="userInput"
            @input="onInput"
            @keyup.enter="sendMessage"
            :placeholder="showPrivacyBanner ? privacyText.btn : translations.placeholder"
            :disabled="showPrivacyBanner"
            class="message-input"
            :class="{ 'input-disabled': showPrivacyBanner }"
          />

          <button
            @click="sendMessage"
            :disabled="!userInput.trim()"
            class="send-btn"
          >
            <i class="fas fa-paper-plane"></i>
          </button>

          <!-- Vorschläge -->
          <div class="suggestions" v-if="filteredSuggestions.length">
            <ul>
              <li
                v-for="(suggestion, index) in filteredSuggestions"
                :key="index"
                @click="selectSuggestion(suggestion.text)"
                :class="{ 'exact-match': suggestion.isExactMatch }"
              >
                {{ suggestion.text }}
                <span
                  v-if="suggestion.score > 0.7"
                  class="match-badge"
                >
                  {{ Math.round(suggestion.score * 100) }}%
                </span>
              </li>
            </ul>
          </div>

        </div>
      </div>

    </div>

  </div>
</template>


<script>
import ChatMessage from './ChatMessage.vue'
import { v4 as uuidv4 } from 'uuid'
import { chatAPI } from '../services/api'
import { faqService } from '../services/faqService'
import { frontendLLMService } from '../services/frontendLLMService'

const SESSION_STORAGE_KEY = 'schuberth_chat_session_id'

// Sprach-Mapping für die UI
const LANGUAGE_MAPPING = {
  de: { label: 'Deutsch', flag: '🇩🇪', apiCode: 'de' },
  en: { label: 'English', flag: '🇬🇧', apiCode: 'en' },
  fr: { label: 'Français', flag: '🇫🇷', apiCode: 'fr' },
  it: { label: 'Italiano', flag: '🇮🇹', apiCode: 'it' },
  es: { label: 'Español', flag: '🇪🇸', apiCode: 'es' },
  pt: { label: 'Português', flag: '🇵🇹', apiCode: 'pt' },
  pl: { label: 'Polski', flag: '🇵🇱', apiCode: 'pl' },

}

// ═══════════════════════════════════════════════════════════════
// DATENSCHUTZHINWEIS – statisch in allen 7 Sprachen (dataguard)
// ═══════════════════════════════════════════════════════════════
const PRIVACY_TEXTS = {
  de: {
    title:   "Datenschutzhinweis",
    body:    "Dieser Chatbot wird KI-gestützt über den Azure OpenAI Service (Microsoft) betrieben. Ihre Eingaben werden zur Bearbeitung Ihrer Anfrage sowie zur Qualitätssicherung verarbeitet und für 6 Monate gespeichert.",
    warning: "Bitte geben Sie keine sensiblen oder vertraulichen personenbezogenen Daten ein (z. B. Passwörter, Gesundheitsdaten, Zahlungsinformationen).",
    link:    "Weitere Informationen finden Sie in unserer",
    linkText:"Datenschutzerklärung",
    section: "(Abschnitt KI-Chatbot).",
    btn:     "Verstanden & Chatbot starten"
  },
  en: {
    title:   "Privacy Notice",
    body:    "This chatbot is powered by AI using the Azure OpenAI Service (Microsoft). Your inputs are processed to answer your request and for quality assurance, and stored for 6 months.",
    warning: "Please do not enter any sensitive or confidential personal data (e.g. passwords, health data, payment information).",
    link:    "For more information, please refer to our",
    linkText:"Privacy Policy",
    section: "(section \"AI Chatbot\").",
    btn:     "Understood & Start Chat"
  },
  fr: {
    title:   "Avis de confidentialité",
    body:    "Ce chatbot est alimenté par l'IA via le service Azure OpenAI (Microsoft). Vos entrées sont traitées pour répondre à votre demande et à des fins d'assurance qualité, et conservées pendant 6 mois.",
    warning: "Veuillez ne pas saisir de données personnelles sensibles ou confidentielles (ex. mots de passe, données de santé, informations de paiement).",
    link:    "Pour plus d'informations, consultez notre",
    linkText:"Politique de confidentialité",
    section: "(section « Chatbot IA »).",
    btn:     "Compris & Démarrer le chat"
  },
  it: {
    title:   "Informativa sulla privacy",
    body:    "Questo chatbot è gestito tramite IA utilizzando il servizio Azure OpenAI (Microsoft). I tuoi input vengono elaborati per rispondere alla tua richiesta e per il controllo qualità, e conservati per 6 mesi.",
    warning: "Si prega di non inserire dati personali sensibili o riservati (es. password, dati sanitari, informazioni di pagamento).",
    link:    "Per ulteriori informazioni, consulta la nostra",
    linkText:"Informativa sulla privacy",
    section: "(sezione \"Chatbot AI\").",
    btn:     "Capito & Avvia la chat"
  },
  es: {
    title:   "Aviso de privacidad",
    body:    "Este chatbot está impulsado por IA a través del servicio Azure OpenAI (Microsoft). Sus entradas se procesan para responder su solicitud y para el control de calidad, y se almacenan durante 6 meses.",
    warning: "Por favor, no introduzca datos personales sensibles o confidenciales (p. ej. contraseñas, datos de salud, información de pago).",
    link:    "Para más información, consulte nuestra",
    linkText:"Política de privacidad",
    section: "(sección \"Chatbot de IA\").",
    btn:     "Entendido & Iniciar chat"
  },
  pt: {
    title:   "Aviso de privacidade",
    body:    "Este chatbot é alimentado por IA através do serviço Azure OpenAI (Microsoft). As suas entradas são processadas para responder ao seu pedido e para garantia de qualidade, e armazenadas durante 6 meses.",
    warning: "Por favor, não insira dados pessoais sensíveis ou confidenciais (ex. senhas, dados de saúde, informações de pagamento).",
    link:    "Para mais informações, consulte a nossa",
    linkText:"Política de privacidade",
    section: "(secção \"Chatbot de IA\").",
    btn:     "Entendido & Iniciar chat"
  },
  pl: {
    title:   "Informacja o prywatności",
    body:    "Ten chatbot jest obsługiwany przez AI za pomocą usługi Azure OpenAI (Microsoft). Twoje dane wejściowe są przetwarzane w celu odpowiedzi na zapytanie i zapewnienia jakości, oraz przechowywane przez 6 miesięcy.",
    warning: "Prosimy nie wprowadzać wrażliwych ani poufnych danych osobowych (np. haseł, danych zdrowotnych, informacji płatniczych).",
    link:    "Więcej informacji znajdziesz w naszej",
    linkText:"Polityce prywatności",
    section: "(sekcja Chatbot AI).",
    btn:     "Rozumiem & Uruchom czat"
  }
}

const TRANSLATIONS = {
 
  de: {
    title: " SCHUBERTH KI-Assistant",
    placeholder: "Frage eingeben...",
    messages: {
      languageChanged: "🌐 Sprache auf {language} umgestellt",
      clearConfirm: "Chat wirklich löschen?",
      welcome: "Willkommen beim SCHUBERTH KI-Assistant! Wie kann ich Ihnen helfen?"
    }
  },
  en: {
    title: " SCHUBERTH AI Assistant", 
    placeholder: "Enter your question...",
    messages: {
      languageChanged: "🌐 Language switched to {language}",
      clearConfirm: "Really clear chat?",
      welcome: "Welcome to the SCHUBERTH AI Assistant! How can I help you?"
    }
  },
  fr: {
    title: " Assistant IA SCHUBERTH",
    placeholder: "Entrez votre question...", 
    messages: {
      languageChanged: "🌐 Langue changée en {language}",
      clearConfirm: "Vraiment effacer le chat?",
      welcome: "Bienvenue dans l'Assistant IA SCHUBERTH ! Comment puis-je vous aider?"
    }
  },
  it: {
    title: " Assistente IA SCHUBERTH",
    placeholder: "Inserisci la tua domanda...",
    messages: {
      languageChanged: "🌐 Lingua cambiata in {language}", 
      clearConfirm: "Cancellare veramente la chat?",
      welcome: "Benvenuto nell'Assistente IA SCHUBERTH! Come posso aiutarti?"
    }
  },
  es: {
    title: " Asistente IA SCHUBERTH",
    placeholder: "Ingresa tu pregunta...",
    messages: {
      languageChanged: "🌐 Idioma cambiado a {language}",
      clearConfirm: "¿Realmente borrar el chat?",
      welcome: "¡Bienvenido al Asistente IA SCHUBERTH! ¿Cómo puedo ayudarte?"
    }
  },
  pt: {
    title: " Assistente IA SCHUBERTH", 
    placeholder: "Digite sua pergunta...",
    messages: {
      languageChanged: "🌐 Idioma alterado para {language}",
      clearConfirm: "Realmente limpar o chat?",
      welcome: "Bem-vindo ao Assistente IA SCHUBERTH! Como posso ajudá-lo?"
    }
  },
  pl: {
    title: " Asystent SI SCHUBERTH",
    placeholder: "Wpisz swoje pytanie...", 
    messages: {
      languageChanged: "🌐 Język zmieniony na {language}",
      clearConfirm: "Czy na pewno wyczyścić czat?",
      welcome: "Witamy w Asystencie SI SCHUBERTH! Jak mogę Ci pomóc?"
    }
  }
}

export default {
  name: 'ChatContainer',
  components: { ChatMessage },
  props: {
    messages: Array,
    isTyping: Boolean
  },
  data() {
    return {
      userInput: '',
      sessionId: null,
      selectedLanguage: 'de',
      showLanguageDropdown: false,
      showPrivacyBanner: false,   // First-Level-Hinweis
      showLegalInfo: false,
      showFeedbackModal: false,
      selectedRating: 0,
      selectedIssues: [],
      customFeedbackText: '',
      dynamicSuggestions: [],
      isLoadingFAQs: false,
      isTranslating: false,
      userIsScrollingUp: false,
      chatSource: 'default',
      
      // Dynamische Übersetzungen
      dynamicTranslations: {
        legal: {},
        feedback: {}
      },
      
      issueOptions: [
        { id: 'too_long', label: 'Antworten zu lang' },
        { id: 'incomplete', label: 'Fehlende Informationen' },
        { id: 'unclear', label: 'Unverständlich formuliert' },
        { id: 'wrong_info', label: 'Falsche Informationen' },
        { id: 'unfriendly', label: 'Unfreundlicher Ton' },
        { id: 'slow', label: 'Zu langsame Antworten' },
        { id: 'other', label: 'Sonstiges' }
      ],
      
      // Verfügbare Sprachen aus dem Mapping
      availableLanguages: Object.entries(LANGUAGE_MAPPING).map(([code, data]) => ({
        value: code,
        label: data.label,
        flag: data.flag,
        apiCode: data.apiCode
      }))
    }
  },
  computed: {
    translations() {
      return TRANSLATIONS[this.selectedLanguage] || TRANSLATIONS.de
    },

    privacyText() {
      return PRIVACY_TEXTS[this.selectedLanguage] || PRIVACY_TEXTS.en
    },
    
    filteredSuggestions() {
      const input = this.userInput.trim().toLowerCase();
      if (!input || input.length < 2) return [];

      if (!this.dynamicSuggestions.length) return [];
      
      // Intelligente Filterlogik
      const scoredSuggestions = this.dynamicSuggestions.map(suggestion => {
        const text = suggestion.toLowerCase();
        let score = 0;
        let isExactMatch = false;

        if (text.startsWith(input)) {
          score = 1.0;
          isExactMatch = true;
        }
        else if (this.containsAllWords(text, input)) {
          score = 0.9;
        }
        else if (this.hasSimilarMeaning(text, input)) {
          score = 0.8;
        }
        else if (this.hasPartialWordMatch(text, input)) {
          score = 0.7;
        }
        else if (text.includes(input)) {
          score = 0.6;
        }
        else if (this.hasPhoneticSimilarity(text, input)) {
          score = 0.5;
        }

        return { text: suggestion, score, isExactMatch };
      });

      return scoredSuggestions
        .filter(item => item.score > 0.4)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);
    }
  },
  
async created() {
  this.initializeSession()
  document.addEventListener('click', this.handleClickOutside)

  // First-Level-Hinweis: prüfen ob Nutzer bereits zugestimmt hat
  const accepted = localStorage.getItem('schuberth_privacy_accepted')
  if (!accepted) {
    this.showPrivacyBanner = true
  }

  // Browser-Sprache automatisch erkennen (falls kein URL-Parameter)
  const urlParams2 = new URLSearchParams(window.location.search)
  const hasLangParam = urlParams2.get('lang') || urlParams2.get('userlang')
  if (!hasLangParam) {
    const browserLang = (navigator.language || navigator.userLanguage || 'de').split('-')[0].toLowerCase()
    if (LANGUAGE_MAPPING[browserLang]) {
      this.selectedLanguage = browserLang
    }
  }




    
  // 🔥 1. ZUERST: Source aus URL-Parameter lesen (von Parent gesetzt)
  const urlParams = new URLSearchParams(window.location.search)
  const sourceFromUrl = urlParams.get('source')
  
  if (sourceFromUrl) {
    // Source-Parameter vorhanden → vom Parent gesetzt
    this.chatSource = sourceFromUrl
    console.log(`✅ Source aus URL-Parameter: ${sourceFromUrl}`)
  } else {
    // 🔥 2. FALLBACK: Automatisch aus Referrer extrahieren
    this.chatSource = this.extractSourceFromUrl()
    console.log(`⚠️ Kein Source-Parameter, verwende automatisch: ${this.chatSource}`)
  }
  
  // Sprache aus URL-Parameter lesen
  const langFromUrl = urlParams.get('lang') || urlParams.get('userlang')
  
  if (langFromUrl && LANGUAGE_MAPPING[langFromUrl]) {
    this.selectedLanguage = langFromUrl
  }

  
  //this.chatSource = this.extractSourceFromUrl();
  //console.log(`🌐 Chat-Source gesetzt auf: ${this.chatSource}`);



  // 🔥 NEU: Sprache aus URL-Parameter lesen mit KORREKTEM Zugriff
  //const urlParams = new URLSearchParams(window.location.search)
  //const langFromUrl = urlParams.get('lang') || urlParams.get('userlang')
  
  // Verwende die globale LANGUAGE_MAPPING Konstante
  // if (langFromUrl && LANGUAGE_MAPPING[langFromUrl]) {
  //   this.selectedLanguage = langFromUrl
  //   //console.log(`🌐 Sprache aus URL übernommen: ${langFromUrl}`)
  // }
  
  // LLM Service Konfiguration prüfen
  await this.checkLLMService()
  
  // Initiale Übersetzungen laden
  await this.loadTranslations(this.selectedLanguage)
  
  // Welcome-Message nur zeigen wenn Datenschutz bereits akzeptiert
  if (!this.showPrivacyBanner) {
    this.$emit('show-welcome-message', this.translations.messages.welcome)
  }
  
  // FAQs für Standardsprache laden
  await this.loadFAQs()
},
  
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  
  methods: {

    onUserScroll() {
    const el = this.$refs.chatBox
    if (!el) return

    const threshold = 80
    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight

    this.userIsScrollingUp = distanceFromBottom > threshold
  },

    acceptPrivacyBanner() {
      localStorage.setItem('schuberth_privacy_accepted', new Date().toISOString())
      this.showPrivacyBanner = false
      // Jetzt erst die Welcome-Message zeigen
      this.$emit('show-welcome-message', this.translations.messages.welcome)
    },
    async checkLLMService() {
      try {
        const health = await frontendLLMService.healthCheck()
        if (health.status === 'healthy') {
          console.log('Service ist bereit')
        } else {
          console.warn('⚠️ Service hat Probleme:', health.error)
        }
      } catch (error) {
        console.error('❌ Service nicht verfügbar:', error)
      }
    },

async loadTranslations(lang) {
  if (lang === 'de') {
    // Für Deutsch keine Übersetzung nötig
    this.dynamicTranslations = this.getDefaultGermanTranslations()
    return
  }

  this.isTranslating = true
  try {
    //console.log(`🔄 Lade Übersetzungen für ${lang}...`)

    // Deutsche Quelltexte
    const germanTexts = this.getDefaultGermanTranslations()

    // **KORREKTUR: Behalte die originale Struktur bei**
    const translations = {
      legal: {},
      feedback: {}
    }

    // 1. Rechtliche Hinweise übersetzen - mit korrekter Struktur
    if (germanTexts.legal) {
      // Übersetze jeden Abschnitt separat
      for (const [sectionKey, sectionContent] of Object.entries(germanTexts.legal)) {
        if (typeof sectionContent === 'string') {
          // Einfache Strings übersetzen
          translations.legal[sectionKey] = await frontendLLMService.translateText(
            sectionContent,
            lang,
            'legal_document'
          )
        } else if (typeof sectionContent === 'object' && sectionContent !== null) {
          // Verschachtelte Objekte (wie privacy, disclaimer)
          translations.legal[sectionKey] = {}
          for (const [subKey, subContent] of Object.entries(sectionContent)) {
            if (typeof subContent === 'string') {
              translations.legal[sectionKey][subKey] = await frontendLLMService.translateText(
                subContent,
                lang,
                'legal_document'
              )
            }
          }
        }
      }
    }

    // 2. Feedback-Texte übersetzen
    if (germanTexts.feedback) {
      for (const [key, value] of Object.entries(germanTexts.feedback)) {
        if (typeof value === 'string') {
          translations.feedback[key] = await frontendLLMService.translateText(
            value,
            lang,
            'user_interface'
          )
        } else if (Array.isArray(value)) {
          // Arrays separat behandeln (z.B. ratingTexts)
          if (key === 'ratingTexts') {
            const translatedArray = []
            for (const text of value) {
              const translated = await frontendLLMService.translateText(
                text,
                lang,
                'rating_text'
              )
              translatedArray.push(translated)
            }
            translations.feedback[key] = translatedArray
          }
        }
      }
    }

    // 3. Issue Options übersetzen (aktualisiere direkt das Array)
    const translatedIssues = []
    for (const issue of this.issueOptions) {
      const translatedLabel = await frontendLLMService.translateText(
        issue.label,
        lang,
        'feedback_issue'
      )
      translatedIssues.push({
        ...issue,
        label: translatedLabel
      })
    }
    this.issueOptions = translatedIssues

    // Speichere die übersetzten Texte
    this.dynamicTranslations = translations

    //console.log('✅ Übersetzungen geladen:', translations)
  } catch (error) {
    console.error('❌ Fehler beim Laden der Übersetzungen:', error)
    // Fallback zu Deutsch
    this.dynamicTranslations = this.getDefaultGermanTranslations()
  } finally {
    this.isTranslating = false
  }
},

   getDefaultGermanTranslations() {
  return {
    legal: {
      title: "Rechtliche Hinweise",
      close: "Schließen",
      privacy: {
        title: "Datenschutz & Haftungsausschluss",
        content: "Die Antworten werden durch einen KI-gestützten Chatbot generiert und basieren auf internen sowie öffentlich verfügbaren Informationen. Trotz sorgfältiger Konzeption kann nicht ausgeschlossen werden, dass Inhalte unvollständig, missverständlich oder fehlerhaft sind. Die bereitgestellten Informationen dienen ausschließlich der allgemeinen Orientierung und stellen keine verbindliche Beratung oder zugesicherte Produkteigenschaft dar. Für Entscheidungen, insbesondere im Zusammenhang mit dem Erwerb von Produkten oder vertraglichen Verpflichtungen, sind ausschließlich die jeweils gültigen offiziellen Unterlagen maßgeblich.",
        feedbackTool: "Feedback-Funktion:",
        feedbackContent: "Im Chatbot ist eine freiwillige Feedback-Funktion integriert. Das abgegebene Feedback wird ausschließlich zur Qualitätssicherung und Weiterentwicklung des Systems verwendet. Sofern Nutzer keine personenbezogenen Angaben machen, erfolgt die Speicherung anonym. Werden personenbezogene Daten freiwillig angegeben, erfolgt deren Verarbeitung gemäß den geltenden Datenschutzbestimmungen.",
        dataProcessing: "Verarbeitung von Daten:",
        dataContent: "Bei der Nutzung des Chatbots werden grundsätzlich keine personenbezogenen Daten aktiv abgefragt. Eine Verarbeitung personenbezogener Daten kann jedoch nicht vollständig ausgeschlossen werden, sofern Nutzer freiwillig entsprechende Informationen in Freitextfeldern eingeben. Es wird empfohlen, keine sensiblen oder personenbezogenen Daten in das Chatfenster einzugeben. Erhobene Daten werden ausschließlich zu internen Analyse- und Optimierungszwecken genutzt. Eine Weitergabe an Dritte erfolgt nicht, es sei denn, dies ist gesetzlich erforderlich oder technisch im Rahmen eines Auftragsverarbeitungsverhältnisses notwendig.",
        gdpr: "Datenschutzrechtliche Hinweise:",
        gdprContent: "Die Verarbeitung personenbezogener Daten erfolgt – soweit einschlägig – im Einklang mit den geltenden datenschutzrechtlichen Bestimmungen. Betroffenenrechte bestehen gemäß den gesetzlichen Vorgaben, sofern personenbezogene Daten verarbeitet werden.",
        security: "Datensicherheit:",
        securityContent: "Es werden angemessene technische und organisatorische Maßnahmen getroffen, um gespeicherte Daten vor unbefugtem Zugriff, Verlust oder Manipulation zu schützen."
      },
      disclaimer: {
        title: "Haftungsausschluss",
        content: "Die Antworten werden durch eine künstliche Intelligenz generiert. Trotz sorgfältiger Entwicklung kann nicht gewährleistet werden, dass sämtliche Inhalte jederzeit vollständig, korrekt und aktuell sind. Die bereitgestellten Informationen dienen ausschließlich einer ersten Orientierung und ersetzen keine individuelle Beratung oder verbindliche Auskunft."
      }
    },
    feedback: {
      title: "Wie zufrieden sind Sie mit dem SCHUBERTH Chatbot?",
      rating: "Gesamtbewertung:",
      ratingTexts: ["Sehr schlecht", "Schlecht", "Okay", "Gut", "Sehr gut"],
      issues: "Was können wir verbessern?",
      placeholderLow: "Weitere Anmerkungen (optional)",
      placeholderHigh: "Was hat Ihnen besonders gut gefallen? (optional)",
      cancel: "Abbrechen",
      submit: "Feedback senden",
      issueOptions: this.issueOptions
    }
  }
},

    async loadFAQs() {
      try {
        this.isLoadingFAQs = true
        //console.log(`🌐 Lade FAQs für Sprache: ${this.selectedLanguage}`)
        
        // FAQs für die aktuell ausgewählte Sprache laden
        const questions = await faqService.getFAQs(this.selectedLanguage)
        this.dynamicSuggestions = questions
        
        //console.log(`✅ ${questions.length} dynamische Vorschläge für ${this.selectedLanguage} geladen`)
      } catch (error) {
        console.error('Fehler beim Laden der FAQs:', error)
        this.dynamicSuggestions = []
      } finally {
        this.isLoadingFAQs = false
      }
    },

    // Sprachwechsel mit FAQ-Reload und Übersetzungen
    async selectLanguage(langCode) {
      const oldLang = this.selectedLanguage
      this.selectedLanguage = langCode
      this.showLanguageDropdown = false
      
      // Übersetzungen laden
      await this.loadTranslations(langCode)
      
      // FAQs für neue Sprache laden
      await this.loadFAQs()
      
      const lang = this.getSelectedLanguage()
      const message = this.translations.messages.languageChanged.replace('{language}', lang.label)
      this.$emit('show-temp-message', message)
      this.$emit('show-welcome-message', this.translations.messages.welcome)
      
      /console.log(`🔄 Sprache geändert: ${oldLang} → ${langCode}`)
    },

    handleScrollToBottom() {
      this.scrollToBottom();
    },
    
    openFeedbackModal() {
      this.showFeedbackModal = true
      this.selectedRating = 0
      this.selectedIssues = []
      this.customFeedbackText = ''
    },

    closeFeedbackModal() {
      this.showFeedbackModal = false
      this.selectedRating = 0
      this.selectedIssues = []
      this.customFeedbackText = ''
    },

    setRating(rating) {
      this.selectedRating = rating
    },

    toggleIssue(issueId) {
      if (this.selectedIssues.includes(issueId)) {
        this.selectedIssues = this.selectedIssues.filter(id => id !== issueId)
      } else {
        this.selectedIssues = [...this.selectedIssues, issueId]
      }
    },

    async submitFeedback() {
      try {
        if (this.selectedRating <= 3 && this.selectedIssues.length === 0) {
          const errorMsg = await frontendLLMService.translateText(
            'Bitte wählen Sie mindestens einen Verbesserungsgrund', 
            this.selectedLanguage,
            'error_message'
          )
          this.$emit('feedback-error', errorMsg)
          return
        }

        const feedbackData = {
          session_id: this.sessionId || 'default',
          query: 'Gesamtbewertung Chatbot',
          answer: 'Allgemeine Chatbot-Erfahrung',
          rating: this.selectedRating,
          issues: this.selectedIssues,
          feedback_text: this.customFeedbackText,
          source: 'chat',
          language: this.selectedLanguage,
          timestamp: new Date().toISOString()
        }
        
        await chatAPI.submitFeedbackToCockpit(feedbackData)
        
        const successMsg = await frontendLLMService.translateText(
          `Vielen Dank für Ihr Feedback! (${this.selectedRating}/5)`, 
          this.selectedLanguage,
          'success_message'
        )
        this.$emit('show-temp-message', successMsg, 'success')
        this.closeFeedbackModal()
        
      } catch (error) {
        console.error('Feedback error:', error)
        const errorMsg = await frontendLLMService.translateText(
          'Fehler beim Senden des Feedbacks', 
          this.selectedLanguage,
          'error_message'
        )
        this.$emit('show-temp-message', errorMsg, 'error')
      }
    },

    getRatingIcon(rating) {
      const icons = [
        'far fa-star',
        'far fa-star',
        'far fa-star', 
        'far fa-star',
        'far fa-star'
      ]
      return icons[rating - 1] || 'far fa-star'
    },

    getRatingText(rating) {
      const defaultTexts = ["Sehr schlecht", "Schlecht", "Okay", "Gut", "Sehr gut"]
      const texts = this.dynamicTranslations.feedback?.ratingTexts || defaultTexts
      return texts[rating - 1] || 'Bitte bewerten'
    },

    toggleLegalInfo() {
      this.showLegalInfo = !this.showLegalInfo
    },

    // Intelligente Suchmethoden
    containsAllWords(text, input) {
      const words = input.split(/\s+/).filter(word => word.length > 2);
      if (words.length === 0) return false;
      return words.every(word => text.includes(word));
    },
    
    hasSimilarMeaning(text, input) {
      const meaningMap = {
        'wie': ['anleitung', 'methode', 'verfahren', 'prozess', 'weise'],
        'was': ['information', 'details', 'erklärung', 'bedeutung'],
        'wo': ['ort', 'platz', 'standort', 'adresse', 'position'],
        'wann': ['zeit', 'datum', 'termin', 'zeitpunkt'],
        'warum': ['grund', 'ursache', 'erklärung'],
        'kann': ['möglichkeit', 'fähigkeit', 'option'],
        'muss': ['pflicht', 'notwendigkeit', 'erforderlich'],
        'funktioniert': ['arbeitet', 'läuft', 'geht', 'funktion'],
        'reparieren': ['repariert', 'reparatur', 'reparier', 'reparierung'],
        'kaputt': ['defekt', 'beschädigt', 'gebrochen', 'zerstört'],
      };

      const inputWords = input.split(/\s+/);
      const textWords = text.split(/\s+/);
      
      for (let inputWord of inputWords) {
        if (meaningMap[inputWord]) {
          const synonyms = meaningMap[inputWord];
          if (synonyms.some(synonym => 
            textWords.some(textWord => 
              textWord.includes(synonym) || synonym.includes(textWord)
            )
          )) {
            return true;
          }
        }
      }
      return false;
    },
    
    hasPartialWordMatch(text, input) {
      const inputWords = input.split(/\s+/);
      const textWords = text.split(/\s+/);
      
      return inputWords.some(inputWord => 
        inputWord.length > 2 &&
        textWords.some(textWord => 
          textWord.includes(inputWord)
        )
      );
    },
    
    hasPhoneticSimilarity(text, input) {
      if (input.length < 3) return false;
      
      const simpleSoundex = (str) => {
        return str
          .replace(/[^a-zäöüß]/g, '')
          .replace(/[aeiouyäöü]/g, '')
          .replace(/(.)\1+/g, '$1')
          .substring(0, 3);
      };
      
      const textSoundex = simpleSoundex(text);
      const inputSoundex = simpleSoundex(input);
      
      return textSoundex === inputSoundex && textSoundex.length >= 2;
    },

    initializeSession() {
      let id = localStorage.getItem(SESSION_STORAGE_KEY)
      if (!id) {
        id = uuidv4()
        localStorage.setItem(SESSION_STORAGE_KEY, id)
      }
      this.sessionId = id
    },
    
    getSelectedLanguage() {
      return this.availableLanguages.find(lang => lang.value === this.selectedLanguage) || this.availableLanguages[0]
    },

    toggleLanguageDropdown(event) {
      if (event) {
        event.stopPropagation();
      }
      this.showLanguageDropdown = !this.showLanguageDropdown;
    },
    
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showLanguageDropdown = false
      }
    },

    onInput() {
      // Automatisch durch computed property filteredSuggestions
    },
    
    selectSuggestion(suggestion) {
      this.userInput = suggestion;
      this.sendMessage();
    },




extractSourceFromUrl() {
  const url = window.parent !== window ? document.referrer : window.location.href;
  if (!url || ['', 'about:blank', 'null'].includes(url)) return 'default';
  return url; // 🔥 Gibt die vollständige URL zurück
},








    
    sendMessage() {
      if (this.userInput.trim()) {
        const query = this.userInput.trim(); 
         
        this.$emit(
          'send-message',
          query,
          this.sessionId, 
          null,
          this.selectedLanguage,
          this.chatSource   // source mitsenden
        ); 
        this.userInput = '';
      }
    },
    
    clearChat() {
      const confirmMessage = this.translations.messages.clearConfirm;
      if (window.confirm(confirmMessage)) {
        this.$emit('clear-chat')
        localStorage.removeItem(SESSION_STORAGE_KEY)
        this.initializeSession()
        this.$emit('show-welcome-message', this.translations.messages.welcome)
      }
    },
    
   scrollToBottom(force = false) {
  this.$nextTick(() => {
    if (this.userIsScrollingUp && !force) return

    const chatBox = this.$refs.chatBox
    if (chatBox) {
      chatBox.scrollTop = chatBox.scrollHeight
    }
  })
}

  },
  
  watch: {
    selectedLanguage(newLang) {
      this.$emit('language-changed', newLang);
    },
    messages: {
      handler() {
        this.scrollToBottom();
      },
      deep: true
    },
    isTyping() {
      this.scrollToBottom();
    }
  },

  emits: [
    'send-message', 
    'clear-chat', 
    'language-changed', 
    'show-welcome-message', 
    'show-temp-message',
    'feedback-error'
  ]
}
</script>
<style scoped>


.bot-avatar{
  padding: 8px;

}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%; 
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgb(255, 255, 255);
  overflow: hidden;
  box-sizing: border-box;
  margin-bottom: 20px;
}

/* === HEADER - Voll nutzung oben === */
.chat-header {
  position: relative; /* Nicht mehr fixed */
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #ef7d00;
  border-bottom: 1px solid var(--border-color);
  border-radius: 0; /* Keine abgerundeten Ecken oben */
  z-index: 1000;
  height: 70px; /* Etwas höher */
  box-sizing: border-box;
  flex-shrink: 0; /* Verhindert Schrumpfen */
}

/* === CHAT MAIN - Nimmt restlichen Platz ein === */
.chat-main {
  display: flex;
  flex-direction: column;
  flex: 1; /* Nimmt verfügbaren Platz */
  min-height: 0; /* Wichtig für korrektes Scrollen */
  overflow: hidden;
  position: relative;
}

/* === CHAT BOX - Scrollbarer Bereich === */
.chat-box {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 1rem 1rem; /* Mehr Padding oben für Willkommensnachricht */
  scrollbar-width: none;
  -ms-overflow-style: none;
  margin-bottom: 0; /* Kein zusätzlicher Abstand */
}

.chat-box::-webkit-scrollbar {
  display: none;
}

/* === CHAT INPUT CONTAINER - Fixiert unten === */
.chat-input-container {
  position: relative; /* Nicht fixed */
  padding: 1rem 1.5rem;
  background: var(--bg-color);
  border-top: 1px solid var(--border-color);
  z-index: 100;
  flex-shrink: 0; /* Verhindert Schrumpfen */
  box-sizing: border-box;
}

/* === LANGUAGE DROPDOWN - Höher positionieren === */
.language-dropdown {
  position: relative;
  margin-bottom: 0.5rem; /* Weniger Abstand */
  display: inline-block;
}

/* === CHAT INPUT - Höher schieben === */
.chat-input {
  display: flex;
  gap: 1rem;
  align-items: center;
  max-width: 100%;
  position: relative;
}

.message-input {
  flex: 1;
  padding: 1rem 1.5rem;
  border: 1px solid var(--border-color);
  border-radius: 25px;
  background: var(--bg-color);
  color: var(--text-color);
  font-size: 1rem;
  outline: none;
  transition: all 0.2s ease;
  min-height: 50px; /* Mindesthöhe */
}

/* === SUGGESTIONS - Direkt über Input === */
.suggestions {
  position: absolute;
  bottom: 100%; /* Direkt über dem Input */
  left: 0;
  right: 0;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15); /* Schatten nach oben */
  backdrop-filter: blur(10px);
  margin-bottom: 5px;
}

/* === RESPONSIVE ANPASSUNGEN === */
@media (max-width: 768px) {
  .chat-header {
    height: 60px;
    padding: 0.75rem 1rem;
  }
  
  .chat-box {
    padding: 15px 0.75rem 0.75rem;
  }
  
  .chat-input-container {
    padding: 0.75rem;
  }
  
  .message-input {
    padding: 0.8rem 1.2rem;
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .chat-header {
    height: 55px;
    padding: 0.5rem 0.75rem;
  }
  
  .chat-header h1 {
    font-size: 1rem;
  }
  
  .chat-box {
    padding: 12px 0.5rem 0.5rem;
  }
  
  .chat-input-container {
     padding-bottom: calc(env(safe-area-inset-bottom) + 0.5rem);
  }
  
  .message-input {
    padding: 0.7rem 1rem;
    font-size: 0.95rem;
    min-height: 45px;
  }
  
  .send-btn {
    width: 30px;
    height: 30px;
  }
}

/* === Zusätzliche Anpassungen für bessere Platzausnutzung === */

/* Besserer Abstand für erste Nachricht (Willkommensnachricht) */
.chat-box > .bot-message:first-child {
  margin-top: 5px; /* Weniger Abstand oben */
}

/* Bot-Nachrichten etwas höher setzen */
.bot-message {
  margin-top: 8px;
  margin-bottom: 8px;
}

/* User-Nachrichten näher an Input setzen */
.user-message {
  margin-top: 8px;
  margin-bottom: 12px; /* Weniger Abstand zum Input */
}

/* Typing Indicator höher positionieren */
.typing-indicator {
  margin-top: 5px;
  margin-bottom: 5px;
}

/* Input-Feld Container kompakter */
.chat-input {
  gap: 0.75rem; /* Weniger Abstand zwischen Input und Button */
}

/* Send-Button kompakter */
.send-btn {
  width: 55px;
  height: 55px;
  min-width: 55px; /* Verhindert Schrumpfen */
}

/* === Scroll-Verhalten verbessern === */
.chat-box:last-child {
  margin-bottom: 10px; /* Weniger Abstand zur unteren Kante */
}

/* Sicherstellen, dass alle Elemente ihren Platz haben */
* {
  box-sizing: border-box;
}

/* === Maximale Platzausnutzung für Header === */
.header-buttons button {
  display: flex;
  align-items: center;
  gap: 0.5rem;                 /* Abstand zwischen Icon und Text */
  padding: 0.6rem 1.2rem;       /* etwas größer */
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 0.6rem;
  cursor: pointer;
  
  /* Glass Effekt */
  background: #ef7d00; /* leicht durchsichtig */
  backdrop-filter: blur(10px);          /* verschwommener Hintergrund */
  -webkit-backdrop-filter: blur(10px);
  
  color: white;        /* Icon/Text behalten ursprüngliche Farbe */
  transition: transform 0.2s ease, background 0.2s ease;
}

.header-buttons button:hover {
  transform: scale(1.05);              /* leichte Vergrößerung beim Hover */
  background: rgba(255, 255, 255, 0.15); /* etwas stärker sichtbar beim Hover */
}

/* Optional: Schatten für besseren Kontrast auf Hintergrund */
.header-buttons button::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 0.6rem;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  pointer-events: none;
}


.icon-button {
  background: none;
  border: none;
  padding: 0.6rem;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

/* === Anpassungen für Modal Overlays === */
.legal-modal-overlay,
.feedback-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

/* === Dark Mode Anpassungen === */
body.dark .chat-container {
  background: var(--bg-dark);
}

body.dark .chat-header {
  background: var(--card-bg-dark);
  border-color: var(--border-dark);
}

body.dark .chat-input-container {
  background: var(--card-bg-dark);
  border-color: var(--border-dark);
}

body.dark .suggestions {
  background: var(--card-bg-dark);
  border-color: var(--border-dark);
}



/* Feedback Modal Styles */
.feedback-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.feedback-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.feedback-modal h4 {
  margin: 0 0 16px 0;
  color: #1f2937;
  font-size: 1.2rem;
  text-align: center;
}

/* Rating Stars */
.rating-section {
  text-align: center;
  margin: 20px 0;
}

.rating-stars {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 10px 0;
}

.star-btn {
  background: none;
  border: none;
  font-size: 2rem;
  color: #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 5px;
}

.star-btn:hover,
.star-btn.active {
  color: #fbbf24;
  transform: scale(1.1);
}

.rating-text {
  display: block;
  color: #6b7280;
  font-size: 1rem;
  margin-top: 5px;
}

/* Issues Grid */
.issues-section {
  margin: 16px 0;
}

.issues-section p {
  margin: 0 0 12px 0;
  color: #6b7280;
  font-size: 1rem;
}

.issues-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
}

.issue-btn {
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  text-align: center;
}

.issue-btn:hover {
  border-color: #3b82f6;
  background: #f8fafc;
}

.issue-btn.selected {
  border-color: #3b82f6;
  background: #3b82f6;
  color: white;
}

/* Custom Feedback */
.custom-feedback {
  margin: 16px 0;
}

.custom-feedback textarea {
  width: 100%;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  resize: vertical;
  font-family: inherit;
  font-size: 1rem;
}

.custom-feedback textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-cancel, .btn-submit {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.btn-cancel {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

.btn-submit {
  background: var(--primary-color);
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background: #002244;
}

.btn-submit:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 600px) {
  .issues-grid {
    grid-template-columns: 1fr;
  }
  
  .feedback-modal {
    padding: 16px;
    margin: 20px;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .star-btn {
    font-size: 1.5rem;
  }
}

/* Legal Modal Styles (bleiben gleich wie vorher) */
.legal-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.legal-modal {
  background: white;
  border-radius: 15px;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.legal-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #e9ecef;
  background: var(--background-light);
  color: #003366;
  border-radius: 15px 15px 0 0;
}

.legal-modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
}

.close-button {
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.legal-content {
  padding: 25px;
}

.legal-section {
  margin-bottom: 30px;
}

.legal-section:last-child {
  margin-bottom: 0;
}

.legal-section h4 {
  color: #003366;
  margin-bottom: 15px;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.legal-section h5 {
  color: #495057;
  margin: 15px 0 8px 0;
  font-size: 1rem;
}

.legal-section p {
  line-height: 1.6;
  color: #6c757d;
  margin-bottom: 10px;
}

.legal-modal-footer {
  padding: 20px 25px;
  border-top: 1px solid #e9ecef;
  text-align: right;
}

.btn-close {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn-close:hover {
  background: #002244;
}

/* Responsive */
@media (max-width: 768px) {
  .legal-modal {
    margin: 10px;
    max-height: 90vh;
  }
  
  .legal-content {
    padding: 20px;
  }
  
  .legal-modal-header {
    padding: 15px 20px;
  }
  
  .legal-modal-header h3 {
    font-size: 1.1rem;
  }
}





/* Responsive */
@media (max-width: 768px) {
  .legal-modal {
    margin: 10px;
    max-height: 90vh;
  }
  
  .legal-content {
    padding: 20px;
  }
  
  .legal-modal-header {
    padding: 15px 20px;
  }
  
  .legal-modal-header h3 {
    font-size: 1.1rem;
  }
}
.language-dropdown {
  position: relative;
  margin-bottom: 0.75rem;
  display: inline-block;
}

.dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--bg-secondary, #f4f4f4);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-color);
}

.dropdown-trigger:hover {
  background: var(--bg-secondary-dark, #e8e8e8);
  border-color: var(--primary-color);
}

.selected-flag {
  font-size: 1.2rem;
  line-height: 1;
}

.dropdown-arrow {
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  color: var(--text-light);
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

/* Dropdown nach oben öffnen */
.dropdown-menu {
  position: absolute;
  bottom: 100%;
  left: 0;
  margin-bottom: 0.25rem;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 160px;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: var(--text-color);
}

.dropdown-item:hover {
  background: var(--bg-secondary, #f4f4f4);
}

.dropdown-item.active {
  background: var(--primary-color);
  color: white;
}

.flag-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.language-name {
  font-size: 1rem;
  font-weight: 500;
}



.chat-header h1 {
  margin: 0;
  color: white;
  font-size: 1.2rem;
}

.header-buttons {
  display: flex;
  gap: 0.5rem;
}

.icon-button {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--primary-color);
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.icon-color{

  color: var(--border-dark);
}

.icon-button:hover {
  background: rgba(0, 0, 0, 0.1);
}


.chat-input {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  max-width: 100%;
  position: relative;
}

.message-input {
  flex: 1;
  padding: 1rem 1.5rem;
  border: 1px solid var(--border-color);
  border-radius: 25px;
  background: var(--bg-color);
  color: var(--text-color);
  font-size: 1.2rem;
  outline: none;
  transition: all 0.2s ease;
}

.message-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(0, 51, 102, 0.1);
}

.send-btn {
  background: var(--accent-color);
  color: white;
  border: none;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  /* SICHERSTELLEN, dass es ein Kreis bleibt */
  min-width: 60px;
  min-height: 60px;
  aspect-ratio: 1 / 1; /* NEU: Verhältnis 1:1 erzwingen */
}

/* In den Media Queries anpassen: */
@media (max-width: 768px) {
  .send-btn {
    width: 50px;    /* Statt 40px */
    height: 50px;   /* Statt 40px - gleiche Werte! */
    min-width: 50px;
    min-height: 50px;
    padding: 0;     /* Padding entfernen, es stört */
  }
}

@media (max-width: 480px) {
  .send-btn {
    width: 45px;    /* Statt unbestimmt */
    height: 45px;   /* Statt unbestimmt - gleiche Werte! */
    min-width: 45px;
    min-height: 45px;
  }
}


.send-btn:disabled {
  background: var(--text-light);
  cursor: not-allowed;
  opacity: 0.6;
}


.suggestions ul {
  list-style: none;
  margin: 0;
  padding: 0.5rem 0;
}

.suggestions li {
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: var(--text-color);
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
}

.suggestions li:hover {
  background-color: var(--bg-secondary);
  border-left-color: var(--primary-color);
}

.suggestions li.exact-match {
  background-color: rgba(0, 102, 204, 0.05);
  border-left-color: var(--primary-color);
  font-weight: 600;
}

.match-badge {
  background: var(--primary-color);
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  margin-left: 0.5rem;
  flex-shrink: 0;
}

/* === Typing Indicator === */
.typing-indicator {
  display: flex;
  gap: 3px;
}

.typing-indicator span {
  width: 6px;
  height: 6px;
  background: var(--accent-color);
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: 0s; }
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-3px); }
}

/* === Dark Mode Anpassungen === */
body.dark .dropdown-trigger {
  background: var(--bg-secondary-dark, #2b2b2b);
  border-color: var(--border-color-dark);
}

body.dark .dropdown-trigger:hover {
  background: var(--bg-secondary-darker, #3a3a3a);
}

body.dark .dropdown-menu {
  background: var(--card-bg-dark);
  border-color: var(--border-color-dark);
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.3);
}

body.dark .dropdown-item:hover {
  background: var(--bg-secondary-dark, #3a3a3a);
}

body.dark .suggestions li.exact-match {
  background-color: rgba(0, 102, 204, 0.15);
}

body.dark .icon-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* === Responsive Design === */
@media (max-width: 768px) {

  .chat-input-container {
    padding: 0.75rem;
  }

  .message-input {
    padding: 0.6rem 0.8rem;
    font-size: 1rem;
  }

  

  .suggestions {
    max-height: 150px;
  }
  
  .suggestions li {
    padding: 0.6rem 0.8rem;
    font-size: 1rem;
  }
  
  .language-dropdown {
    margin-bottom: 0.5rem;
  }
  
  .dropdown-trigger {
    padding: 0.4rem 0.6rem;
    font-size: 0.9rem;
  }
  
  .dropdown-menu {
    min-width: 140px;
  }
  
  .dropdown-item {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
  }
  
  .flag-icon {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {

  
  .chat-header {
    padding: 0.75rem;
  }
  
  .chat-header h1 {
    font-size: 1.1rem;
  }
  
  .chat-box {
    padding: 0.75rem;
  }
  
  .suggestions {
    max-height: 120px;
  }
  
  .suggestions li {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }
  
  .match-badge {
    font-size: 0.65rem;
    padding: 0.15rem 0.4rem;
  }
}

/* ═══════════════════════════════════════════════
   INLINE DATENSCHUTZHINWEIS
   ═══════════════════════════════════════════════ */
.privacy-inline-banner {
  margin: 12px 8px 20px;
  border-radius: 14px;
  overflow: hidden;
  border: 1.5px solid #ef7d00;
  background: #fff;
  box-shadow: 0 4px 20px rgba(239, 125, 0, 0.12);
  animation: bannerFadeIn 0.4s ease;
}

@keyframes bannerFadeIn {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}

.privacy-inline-header {
  background: #ef7d00;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-weight: 700;
  font-size: 1rem;
}

.privacy-inline-header i {
  font-size: 1.1rem;
}

.privacy-inline-body {
  padding: 16px;
}

.privacy-inline-body p {
  font-size: 0.88rem;
  line-height: 1.6;
  color: #374151;
  margin: 0 0 10px 0;
}

.privacy-inline-body p:last-child {
  margin-bottom: 0;
}

.privacy-inline-warning {
  background: #fff8f0;
  border-left: 3px solid #ef7d00;
  border-radius: 0 6px 6px 0;
  padding: 8px 12px !important;
  color: #92400e !important;
  display: flex !important;
  align-items: flex-start;
  gap: 8px;
}

.privacy-inline-warning i {
  color: #ef7d00;
  margin-top: 2px;
  flex-shrink: 0;
}

.privacy-inline-link {
  color: #ef7d00;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.privacy-inline-link:hover {
  color: #c96500;
}

.privacy-inline-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: calc(100% - 32px);
  margin: 0 16px 16px;
  padding: 12px;
  background: #ef7d00;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
}

.privacy-inline-btn:hover {
  background: #c96500;
  transform: translateY(-1px);
}

.privacy-inline-btn:active {
  transform: translateY(0);
}

/* Input gesperrt wenn Banner sichtbar */
.input-disabled {
  background: #f9fafb !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
  border-color: #e5e7eb !important;
}
</style>